
import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Link,
  useSearchParams
} from "react-router-dom";

import {
  Card,
  Row,
  Col,
  Spinner,
  Button,
  Modal,
  Container,
  Form
} from "react-bootstrap";

import axios from "axios";

import {
  useCart
} from "../context/CartContext";

import HeroCarousel from "../components/HeroCarousel";
import TrustBar from "../components/TrustBar";


/* ============================================================
   API CONFIGURATION
   ============================================================

   Development:
   https://apimyshop.myidealtd.co.uk/api

   Production:
   https://apimyshop.myidealtd.co.uk/api

   IMPORTANT:
   API_URL is for API requests.

   IMAGE_BASE_URL is for images.

   API_URL:
   https://apimyshop.myidealtd.co.uk/api

   IMAGE_BASE_URL:
   https://apimyshop.myidealtd.co.uk
   ============================================================ */

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://apimyshop.myidealtd.co.uk/api"
)
  .trim()
  .replace(/\/+$/, "");


const IMAGE_BASE_URL = API_URL
  .replace(/\/api\/?$/i, "")
  .replace(/\/+$/, "");


const DEFAULT_IMAGE =
  "/images/no-image.png";


/* ============================================================
   PRODUCTS
   ============================================================ */

function Products() {

  const {
    addToCart,
    cart
  } = useCart();


  const safeCart =
    Array.isArray(cart)
      ? cart
      : [];


  /* ============================================================
     SEARCH PARAMS
     ============================================================ */

  const [
    searchParams,
    setSearchParams
  ] = useSearchParams();


  const searchTerm =
    searchParams.get("q") || "";


  const selectedCategory =
    searchParams.get("category") || "All";


  /* ============================================================
     STATES
     ============================================================ */

  const [
    products,
    setProducts
  ] = useState([]);


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    error,
    setError
  ] = useState("");


  const [
    priceRange,
    setPriceRange
  ] = useState("ALL");


  const [
    page,
    setPage
  ] = useState(1);


  const [
    quickView,
    setQuickView
  ] = useState(null);


  const [
    showModal,
    setShowModal
  ] = useState(false);


  const [
    newsletterEmail,
    setNewsletterEmail
  ] = useState("");


  const [
    subscribed,
    setSubscribed
  ] = useState(false);


  /* ============================================================
     DARK MODE
     ============================================================ */

  const [
    darkMode,
    setDarkMode
  ] = useState(() => {

    return (
      localStorage.getItem("darkMode") === "true"
    );

  });


  useEffect(() => {

    localStorage.setItem(
      "darkMode",
      darkMode
    );

  }, [darkMode]);


  /* ============================================================
     DEBUG API / IMAGE CONFIGURATION
     ============================================================ */

  useEffect(() => {

    console.log(
      "=========================================="
    );

    console.log(
      "Products API CONFIG"
    );

    console.log(
      "API_URL:",
      API_URL
    );

    console.log(
      "IMAGE_BASE_URL:",
      IMAGE_BASE_URL
    );

    console.log(
      "=========================================="
    );

  }, []);


  /* ============================================================
     IMAGE URL HELPER

     Supports:

     1. Full URL
        https://example.com/image.jpg

     2. Image object
        {
          main: "/uploads/products/test.jpg",
          original: "...",
          thumbnail: "...",
          cart: "..."
        }

     3. /uploads/products/test.jpg

     4. uploads/products/test.jpg

     5. products/test.jpg

     6. images/test.jpg

     7. test.jpg

     8. /api/uploads/products/test.jpg

     ============================================================ */

  const getImageUrl = (img) => {

    /* ----------------------------------------------------------
       No image
       ---------------------------------------------------------- */

    if (!img) {

      return DEFAULT_IMAGE;

    }


    /* ----------------------------------------------------------
       IMAGE OBJECT

       Prefer main image first.
       ---------------------------------------------------------- */

    if (typeof img === "object") {

      img =
        img.main ||
        img.original ||
        img.thumbnail ||
        img.cart ||
        img.url ||
        img.path ||
        img.filename ||
        "";

    }


    /* ----------------------------------------------------------
       Still no image
       ---------------------------------------------------------- */

    if (!img) {

      return DEFAULT_IMAGE;

    }


    /* ----------------------------------------------------------
       Convert to string
       ---------------------------------------------------------- */

    img =
      String(img).trim();


    if (!img) {

      return DEFAULT_IMAGE;

    }


    /* ----------------------------------------------------------
       Full HTTP / HTTPS URL

       Return unchanged.
       ---------------------------------------------------------- */

    if (
      img.startsWith("http://") ||
      img.startsWith("https://")
    ) {

      return img;

    }


    /* ----------------------------------------------------------
       Remove leading slash

       /uploads/a.jpg

       becomes:

       uploads/a.jpg
       ---------------------------------------------------------- */

    img =
      img.replace(/^\/+/, "");


    /* ----------------------------------------------------------
       Remove accidental API prefix

       api/uploads/a.jpg

       becomes:

       uploads/a.jpg
       ---------------------------------------------------------- */

    img =
      img.replace(/^api\/+/i, "");


    /* ----------------------------------------------------------
       Remove accidental /api/ prefix

       /api/uploads/a.jpg

       becomes:

       uploads/a.jpg
       ---------------------------------------------------------- */

    img =
      img.replace(/^\/?api\/+/i, "");


    /* ----------------------------------------------------------
       uploads/...

       https://apimyshop.myidealtd.co.uk/uploads/...
       ---------------------------------------------------------- */

    if (
      img
        .toLowerCase()
        .startsWith("uploads/")
    ) {

      return `${IMAGE_BASE_URL}/${img}`;

    }


    /* ----------------------------------------------------------
       products/...

       https://apimyshop.myidealtd.co.uk/uploads/products/...
       ---------------------------------------------------------- */

    if (
      img
        .toLowerCase()
        .startsWith("products/")
    ) {

      return `${IMAGE_BASE_URL}/uploads/${img}`;

    }


    /* ----------------------------------------------------------
       images/...

       https://apimyshop.myidealtd.co.uk/images/...
       ---------------------------------------------------------- */

    if (
      img
        .toLowerCase()
        .startsWith("images/")
    ) {

      return `${IMAGE_BASE_URL}/${img}`;

    }


    /* ----------------------------------------------------------
       Plain filename

       product.jpg

       becomes:

       https://apimyshop.myidealtd.co.uk/uploads/product.jpg
       ---------------------------------------------------------- */

    return `${IMAGE_BASE_URL}/uploads/${img}`;

  };


  /* ============================================================
     IMAGE ERROR HANDLER
     ============================================================ */

  const handleImageError = (e, product = null) => {

    const failedUrl =
      e.currentTarget.src;


    console.error(
      "=========================================="
    );

    console.error(
      "PRODUCT IMAGE FAILED"
    );

    console.error(
      "Failed URL:",
      failedUrl
    );

    if (product) {

      console.error(
        "Product:",
        product.name
      );

      console.error(
        "Image data:",
        product.images
      );

    }

    console.error(
      "=========================================="
    );


    /* ----------------------------------------------------------
       Prevent infinite fallback loop
       ---------------------------------------------------------- */

    if (
      failedUrl.includes(DEFAULT_IMAGE)
    ) {

      return;

    }


    e.currentTarget.onerror = null;

    e.currentTarget.src =
      DEFAULT_IMAGE;

  };


  /* ============================================================
     LOAD PRODUCTS
     ============================================================ */

  useEffect(() => {

    let mounted = true;


    const fetchProducts = async () => {

      try {

        setLoading(true);

        setError("");


        const endpoint =
          `${API_URL}/products`;


        console.log(
          "Products REQUEST:",
          endpoint
        );


        const res =
          await axios.get(
            endpoint
          );


        console.log(
          "Products RESPONSE:",
          res.data
        );


        if (!mounted) {

          return;

        }


        /* ------------------------------------------------------
           Backend may return:

           {
             products: [...]
           }

           OR:

           [...]
           ------------------------------------------------------ */

        const productData =
          Array.isArray(res.data)
            ? res.data
            : res.data?.products ||
              res.data?.data?.products ||
              [];


        setProducts(
          Array.isArray(productData)
            ? productData
            : []
        );

      }

      catch (err) {

        console.error(
          "Products fetch error:",
          err
        );

        if (!mounted) {

          return;

        }


        setError(
          err.response?.data?.message ||
          "Failed to load products"
        );

      }

      finally {

        if (mounted) {

          setLoading(false);

        }

      }

    };


    fetchProducts();


    return () => {

      mounted = false;

    };

  }, []);


  /* ============================================================
     CATEGORY SCROLL
     ============================================================ */

  useEffect(() => {

    if (
      !selectedCategory ||
      selectedCategory === "All"
    ) {

      return;

    }


    const timer =
      setTimeout(() => {

        const productsSection =
          document.getElementById(
            "products-section"
          );


        if (!productsSection) {

          return;

        }


        productsSection.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });

      }, 100);


    return () =>
      clearTimeout(timer);

  }, [selectedCategory]);


  /* ============================================================
     RESET PAGE WHEN FILTER CHANGES
     ============================================================ */

  useEffect(() => {

    setPage(1);

  }, [
    selectedCategory,
    searchTerm,
    priceRange
  ]);


  /* ============================================================
     USER
     ============================================================ */

  const user = (() => {

    try {

      return JSON.parse(
        localStorage.getItem("user")
      );

    }

    catch {

      return null;

    }

  })();


  const isAdmin =
    user?.role === "admin" ||
    user?.email === "admin" ||
    user?.email === "testadmin";


  /* ============================================================
     CATEGORIES
     ============================================================ */

  const categories =
    useMemo(() => {

      const cats =
        products
          .map(p => {

            if (
              typeof p.category === "object"
            ) {

              return p.category?.name;

            }

            return p.category;

          })
          .filter(Boolean);


      return [
        "All",
        ...new Set(cats)
      ];

    }, [products]);


  /* ============================================================
     PRICE FILTER
     ============================================================ */

  const applyPriceFilter =
    (product) => {

      const price =
        Number(product.price);


      switch (priceRange) {

        case "0-25":

          return price <= 25;


        case "25-50":

          return (
            price > 25 &&
            price <= 50
          );


        case "50-100":

          return (
            price > 50 &&
            price <= 100
          );


        case "100+":

          return price > 100;


        default:

          return true;

      }

    };


  /* ============================================================
     FILTERED PRODUCTS
     ============================================================ */

  const filtered =
    useMemo(() => {

      let list =
        products.filter(
          p =>
            p.features?.active !== false
        );


      /* --------------------------------------------------------
         CATEGORY
         -------------------------------------------------------- */

      if (
        selectedCategory !== "All"
      ) {

        list =
          list.filter(p => {

            const category =
              typeof p.category === "object"
                ? p.category?.name
                : p.category;


            return (
              category ===
              selectedCategory
            );

          });

      }


      /* --------------------------------------------------------
         SEARCH
         -------------------------------------------------------- */

      if (searchTerm) {

        const q =
          searchTerm.toLowerCase();


        list =
          list.filter(p => {

            const name =
              p.name?.toLowerCase() || "";


            const brand =
              typeof p.brand === "object"
                ? p.brand?.name?.toLowerCase()
                : p.brand?.toLowerCase();


            const category =
              typeof p.category === "object"
                ? p.category?.name?.toLowerCase()
                : p.category?.toLowerCase();


            return (
              name.includes(q) ||
              brand?.includes(q) ||
              category?.includes(q)
            );

          });

      }


      /* --------------------------------------------------------
         PRICE
         -------------------------------------------------------- */

      list =
        list.filter(
          applyPriceFilter
        );


      return list;

    }, [
      products,
      selectedCategory,
      priceRange,
      searchTerm
    ]);


  /* ============================================================
     FEATURED
     ============================================================ */

  const featured =
    useMemo(() => {

      return products.filter(
        p =>
          p.featured === true
      );

    }, [products]);


  /* ============================================================
     TRENDING
     ============================================================ */

  const trendingProducts =
    useMemo(() => {

      return products.filter(
        p =>
          p.trending === true
      );

    }, [products]);


  /* ============================================================
     NEW ARRIVALS
     ============================================================ */

  const newArrivals =
    useMemo(() => {

      return products.filter(
        p =>
          p.newArrival === true
      );

    }, [products]);


  /* ============================================================
     BEST SELLERS
     ============================================================ */

  const bestSellers =
    useMemo(() => {

      return products.filter(
        p =>
          p.bestSeller === true
      );

    }, [products]);


  /* ============================================================
     SALE PRODUCTS
     ============================================================ */

  const saleProducts =
    useMemo(() => {

      return products.filter(
        p =>
          p.onSale === true
      );

    }, [products]);


  /* ============================================================
     FEATURE BADGES
     ============================================================ */

  const getFeatureBadges =
    (product) => {

      const badges = [];


      if (product.featured) {

        badges.push(
          "⭐ FEATURED"
        );

      }


      if (product.newArrival) {

        badges.push(
          "🆕 NEW"
        );

      }


      if (product.bestSeller) {

        badges.push(
          "🔥 BEST SELLER"
        );

      }


      if (product.onSale) {

        badges.push(
          "🏷️ SALE"
        );

      }


      if (product.trending) {

        badges.push(
          "🚀 TRENDING"
        );

      }


      return badges;

    };


  /* ============================================================
     NEWSLETTER
     ============================================================ */

  const handleNewsletterSubscribe =
    async (e) => {

      e.preventDefault();


      try {

        await axios.post(
          `${API_URL}/newsletter/subscribe`,
          {
            email:
              newsletterEmail
          }
        );


        setSubscribed(true);

        setNewsletterEmail("");

      }

      catch (error) {

        console.error(
          "Newsletter error:",
          error
        );


        alert(
          error.response?.data?.message ||
          "Subscription failed"
        );

      }

    };


  /* ============================================================
     ADD TO CART
     ============================================================ */

  const handleAdd =
    (product) => {

      if (isAdmin) {

        setShowModal(true);

        return;

      }


      if (
        safeCart.some(
          item =>
            String(
              item?._id ||
              item?.id
            ) ===
            String(
              product?._id ||
              product?.id
            )
        )
      ) {

        return;

      }


      addToCart(product);

    };


  /* ============================================================
     PRODUCT CARD
     ============================================================ */

  const ProductCard =
    ({
      p,
      badge
    }) => {

      const imageUrl =
        getImageUrl(
          p?.images?.[0]
        );


      const isOutOfStock =
        Number(p?.stock) <= 0;


      return (

        <Card
          style={{
            border: "none",
            borderRadius: "28px",
            overflow: "hidden",
            background:
              darkMode
                ? "#111827"
                : "#ffffff",
            boxShadow:
              "0 15px 40px rgba(0,0,0,.10)",
            height: "100%",
            transition:
              "0.35s"
          }}

          onMouseEnter={(e) => {

            e.currentTarget.style.transform =
              "translateY(-8px)";

          }}

          onMouseLeave={(e) => {

            e.currentTarget.style.transform =
              "translateY(0)";

          }}

        >

          {/* IMAGE */}

          <div
            style={{
              position: "relative"
            }}
          >

            {/* BADGE */}

            {badge && (

              <div
                style={{
                  position: "absolute",
                  top: "15px",
                  left: "15px",
                  zIndex: 2,
                  background:
                    "linear-gradient(135deg,#2563eb,#7c3aed)",
                  color: "#fff",
                  padding:
                    "7px 14px",
                  borderRadius:
                    "999px",
                  fontSize: "12px",
                  fontWeight: 700
                }}
              >

                {badge}

              </div>

            )}


            {/* OUT OF STOCK */}

            {isOutOfStock && (

              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "rgba(0,0,0,.45)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "18px"
                }}
              >

                OUT OF STOCK

              </div>

            )}


            {/* PRODUCT LINK */}

            <Link
              to={`/product/${p._id}`}
              style={{
                display: "block"
              }}
            >

              <img
                src={imageUrl}

                alt={
                  p?.name ||
                  "Product"
                }

                loading="lazy"

                onError={(e) =>
                  handleImageError(
                    e,
                    p
                  )
                }

                style={{
                  width: "100%",
                  height: "260px",
                  objectFit: "contain",
                  padding: "20px",
                  background:
                    darkMode
                      ? "#0f172a"
                      : "#f8fafc"
                }}
              />

            </Link>

          </div>


          {/* BODY */}

          <Card.Body>

            {/* CATEGORY */}

            {p.category && (

              <div
                style={{
                  fontSize: "12px",
                  color:
                    darkMode
                      ? "#94a3b8"
                      : "#64748b",
                  marginBottom: "6px"
                }}
              >

                {typeof p.category === "object"
                  ? p.category.name
                  : p.category}

              </div>

            )}


            {/* NAME */}

            <h5
              style={{
                fontWeight: 700,
                color:
                  darkMode
                    ? "#fff"
                    : "#111827"
              }}
            >

              {p.name}

            </h5>


            {/* PRICE */}

            <div
              style={{
                fontSize: "22px",
                fontWeight: 800,
                color: "#16a34a"
              }}
            >

              £
              {Number(
                p.price || 0
              ).toFixed(2)}

            </div>


            {/* STOCK */}

            {typeof p.stock !==
              "undefined" && (

              <div
                style={{
                  marginTop: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color:
                    isOutOfStock
                      ? "#dc2626"
                      : "#16a34a"
                }}
              >

                {isOutOfStock
                  ? "Out of stock"
                  : `${p.stock} in stock`}

              </div>

            )}


            {/* QUICK VIEW */}

            <Button
              variant="outline-secondary"

              onClick={() =>
                setQuickView(p)
              }

              style={{
                width: "100%",
                marginTop: "15px",
                borderRadius: "14px",
                fontWeight: 600
              }}
            >

              Quick View

            </Button>


            {/* ADD CART */}

            <Button
              onClick={() =>
                handleAdd(p)
              }

              disabled={
                isOutOfStock ||
                safeCart.some(
                  item =>
                    String(
                      item?._id ||
                      item?.id
                    ) ===
                    String(
                      p?._id ||
                      p?.id
                    )
                )
              }

              style={{
                width: "100%",
                marginTop: "10px",
                border: "none",
                borderRadius: "14px",
                background:
                  "linear-gradient(135deg,#2563eb,#3b82f6)",
                fontWeight: 700
              }}
            >

              {isOutOfStock
                ? "Out of Stock"
                : safeCart.some(
                    item =>
                      String(
                        item?._id ||
                        item?.id
                      ) ===
                      String(
                        p?._id ||
                        p?.id
                      )
                  )
                  ? "Already in Cart"
                  : "Add To Cart"}

            </Button>

          </Card.Body>

        </Card>

      );

    };


  /* ============================================================
     PAGINATION
     ============================================================ */

  const PRODUCTS_PER_PAGE = 8;


  const totalPages =
    Math.ceil(
      filtered.length /
      PRODUCTS_PER_PAGE
    );


  const paginated =
    filtered.slice(
      (page - 1) *
        PRODUCTS_PER_PAGE,

      page *
        PRODUCTS_PER_PAGE
    );


  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {

    return (

      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background:
            darkMode
              ? "#020617"
              : "#f5f7fb"
        }}
      >

        <Spinner
          animation="border"
        />

      </div>

    );

  }


  /* ============================================================
     PAGE
     ============================================================ */

  return (

    <div
      style={{
        background:
          darkMode
            ? "#020617"
            : "#f5f7fb",
        minHeight: "100vh",
        transition: "0.3s"
      }}
    >

      {/* ======================================================
          DARK MODE
          ====================================================== */}

      <Button
        onClick={() =>
          setDarkMode(!darkMode)
        }

        style={{
          position: "fixed",
          right: "20px",
          bottom: "20px",
          zIndex: 9999,
          borderRadius: "999px",
          border: "none",
          padding:
            "12px 20px",
          background:
            darkMode
              ? "#fff"
              : "#111827",
          color:
            darkMode
              ? "#111827"
              : "#fff",
          fontWeight: 700,
          boxShadow:
            "0 10px 30px rgba(0,0,0,.25)"
        }}
      >

        {darkMode
          ? "☀️ Light"
          : "🌙 Dark"}

      </Button>


      {/* ======================================================
          ADMIN MODAL
          ====================================================== */}

      <Modal
        show={showModal}
        onHide={() =>
          setShowModal(false)
        }
        centered
      >

        <Modal.Header
          closeButton
        >

          <Modal.Title>
            Access Restricted
          </Modal.Title>

        </Modal.Header>


        <Modal.Body
          className="text-center"
        >

          🚫 Admin users cannot
          add products to cart.

        </Modal.Body>


        <Modal.Footer>

          <Button
            variant="dark"
            onClick={() =>
              setShowModal(false)
            }
          >

            OK

          </Button>

        </Modal.Footer>

      </Modal>


      {/* ======================================================
          QUICK VIEW
          ====================================================== */}

      <Modal
        show={!!quickView}
        onHide={() =>
          setQuickView(null)
        }
        size="lg"
        centered
      >

        {quickView && (

          <>

            <Modal.Header
              closeButton
            >

              <Modal.Title>

                {quickView.name}

              </Modal.Title>

            </Modal.Header>


            <Modal.Body>

              <Row className="g-4">

                <Col md={6}>

                  <img
                    src={getImageUrl(
                      quickView?.images?.[0]
                    )}

                    alt={
                      quickView.name
                    }

                    loading="eager"

                    onError={(e) =>
                      handleImageError(
                        e,
                        quickView
                      )
                    }

                    style={{
                      width: "100%",
                      height: "400px",
                      borderRadius: "20px",
                      objectFit: "contain",
                      background:
                        "#f8fafc"
                    }}
                  />

                </Col>


                <Col md={6}>

                  <h2>

                    £
                    {Number(
                      quickView.price ||
                      0
                    ).toFixed(2)}

                  </h2>


                  <p>

                    {quickView.description ||
                      "No description available."}

                  </p>


                  <Button
                    onClick={() => {

                      handleAdd(
                        quickView
                      );

                      setQuickView(
                        null
                      );

                    }}
                  >

                    Add To Cart

                  </Button>

                </Col>

              </Row>

            </Modal.Body>

          </>

        )}

      </Modal>


      {/* ======================================================
          HERO
          ====================================================== */}

      <HeroCarousel />

      <TrustBar />


      {/* ======================================================
          DEMO CTA
          ====================================================== */}

      <Container
        fluid
        className="py-4"
      >

        <div
          style={{
            background:
              "linear-gradient(135deg,#2563eb,#3b82f6)",
            borderRadius: "28px",
            padding: "35px",
            color: "#fff",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
            boxShadow:
              "0 20px 50px rgba(37,99,235,.25)"
          }}
        >

          <div>

            <h2
              style={{
                fontWeight: 800
              }}
            >

              Modern Ecommerce
              Experience

            </h2>

            <p>

              React • Node.js • Stripe
              • Modern UI/UX

            </p>

          </div>


          <a
            href="https://startup-ecommerce.myidealtd.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "#fff",
              color: "#111827",
              padding:
                "14px 25px",
              borderRadius:
                "999px",
              fontWeight: 700,
              textDecoration:
                "none"
            }}
          >

            Request Your Store →

          </a>

        </div>

      </Container>


      {/* ======================================================
          FEATURED
          ====================================================== */}

      {featured.length > 0 && (

        <Container
          fluid
          className="py-5"
        >

          <div
            className="text-center mb-5"
          >

            <h2
              style={{
                fontSize:
                  "clamp(2rem,4vw,3rem)",
                fontWeight: 800,
                color:
                  darkMode
                    ? "#fff"
                    : "#111827"
              }}
            >

              ⭐ Featured Collection

            </h2>


            <p
              style={{
                color:
                  darkMode
                    ? "#cbd5e1"
                    : "#6b7280"
              }}
            >

              Hand-picked products
              selected by our team

            </p>

          </div>


          <Row className="g-4">

            {featured
              .slice(0, 8)
              .map(p => (

                <Col
                  lg={3}
                  md={6}
                  key={p._id}
                >

                  <ProductCard
                    p={p}
                    badge="⭐ FEATURED"
                  />

                </Col>

              ))}

          </Row>

        </Container>

      )}


      {/* ======================================================
          CATEGORIES
          ====================================================== */}

      <Container
        fluid
        className="py-5"
        id="categories-section"
      >

        <div
          className="text-center mb-5"
        >

          <h2
            style={{
              fontWeight: 800,
              color:
                darkMode
                  ? "#fff"
                  : "#111827"
            }}
          >

            🛍 Shop By Category

          </h2>

        </div>


        <Row className="g-4">

          {categories
            .filter(c => c !== "All")
            .slice(0, 8)
            .map(
              (cat, index) => (

                <Col
                  lg={3}
                  md={4}
                  sm={6}
                  key={cat}
                >

                  <Card
                    onClick={() => {

                      setPage(1);

                      setSearchParams(
                        prev => {

                          const params =
                            new URLSearchParams(
                              prev
                            );

                          params.set(
                            "category",
                            cat
                          );

                          params.delete(
                            "page"
                          );

                          return params;

                        }
                      );

                    }}

                    style={{
                      border: "none",
                      borderRadius:
                        "28px",
                      cursor: "pointer",
                      background:
                        darkMode
                          ? "#111827"
                          : "#fff",
                      boxShadow:
                        "0 15px 40px rgba(0,0,0,.08)"
                    }}
                  >

                    <Card.Body
                      className="text-center"
                    >

                      <div
                        style={{
                          fontSize:
                            "45px",
                          marginBottom:
                            "15px"
                        }}
                      >

                        {
                          [
                            "🛒",
                            "📱",
                            "💻",
                            "🎧",
                            "👕",
                            "🏠",
                            "⌚",
                            "🎮"
                          ][index]
                        }

                      </div>


                      <h4
                        style={{
                          fontWeight: 800,
                          color:
                            darkMode
                              ? "#fff"
                              : "#111827"
                        }}
                      >

                        {cat}

                      </h4>


                      <p
                        style={{
                          color:
                            "#6b7280"
                        }}
                      >

                        {
                          products.filter(
                            p => {

                              const category =
                                typeof p.category ===
                                "object"
                                  ? p.category?.name
                                  : p.category;

                              return (
                                category ===
                                cat
                              );

                            }
                          ).length
                        }

                        {" "}Products

                      </p>

                    </Card.Body>

                  </Card>

                </Col>

              )
            )}

        </Row>

      </Container>


      {/* ======================================================
          TRENDING
          ====================================================== */}

      {trendingProducts.length > 0 && (

        <Container
          fluid
          className="py-5"
          id="trending-section"
        >

          <h2
            style={{
              fontWeight: 800,
              color:
                darkMode
                  ? "#fff"
                  : "#111827"
            }}
          >

            🔥 Trending Now

          </h2>


          <Row
            className="g-4 mt-3"
          >

            {trendingProducts
              .slice(0, 8)
              .map(p => (

                <Col
                  lg={3}
                  md={6}
                  key={p._id}
                >

                  <ProductCard
                    p={p}
                    badge="🚀 TRENDING"
                  />

                </Col>

              ))}

          </Row>

        </Container>

      )}


      {/* ======================================================
          CUSTOMER REVIEWS
          ====================================================== */}

      <Container
        fluid
        className="py-5"
        id="reviews-section"
      >

        <div
          className="text-center mb-5"
        >

          <h2
            style={{
              fontWeight: 800,
              color:
                darkMode
                  ? "#fff"
                  : "#111827"
            }}
          >

            ★★★★★ Customer Reviews

          </h2>


          <p
            style={{
              color:
                darkMode
                  ? "#cbd5e1"
                  : "#6b7280"
            }}
          >

            Trusted by thousands
            of happy customers

          </p>

        </div>


        <Row className="g-4">

          {[
            {
              name:
                "Sarah Williams",
              text:
                "Excellent quality and very fast delivery."
            },
            {
              name:
                "James Carter",
              text:
                "Secure checkout and fantastic service."
            },
            {
              name:
                "Emily Brown",
              text:
                "Beautiful products. Will buy again."
            }
          ].map(
            (r, index) => (

              <Col
                lg={4}
                key={index}
              >

                <Card
                  style={{
                    border: "none",
                    borderRadius:
                      "28px",
                    background:
                      darkMode
                        ? "#111827"
                        : "#fff",
                    boxShadow:
                      "0 15px 40px rgba(0,0,0,.08)"
                  }}
                >

                  <Card.Body>

                    <div>
                      ⭐⭐⭐⭐⭐
                    </div>


                    <h5
                      style={{
                        fontWeight: 700
                      }}
                    >

                      {r.name}

                    </h5>


                    <p
                      style={{
                        color:
                          "#6b7280"
                      }}
                    >

                      "{r.text}"

                    </p>

                  </Card.Body>

                </Card>

              </Col>

            )
          )}

        </Row>

      </Container>


      {/* ======================================================
          PRODUCTS
          ====================================================== */}

      <Container
        fluid
        className="py-5"
        id="products-section"
        style={{
          scrollMarginTop:
            "90px"
        }}
      >

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            flexWrap:
              "wrap",
            gap: "15px"
          }}
        >

          <div>

            <h2
              style={{
                fontWeight: 800,
                color:
                  darkMode
                    ? "#fff"
                    : "#111827"
              }}
            >

              🛍 Explore Products

            </h2>


            <p
              style={{
                color:
                  darkMode
                    ? "#94a3b8"
                    : "#6b7280"
              }}
            >

              {filtered.length}
              {" "}products found

            </p>

          </div>


          <Button
            as={Link}
            to="/shop"
            style={{
              border: "none",
              borderRadius:
                "999px",
              padding:
                "12px 25px",
              background:
                "linear-gradient(135deg,#2563eb,#3b82f6)",
              fontWeight: 700
            }}
          >

            View All Products →

          </Button>

        </div>


        {/* ====================================================
            FILTERS
            ==================================================== */}

        <Row className="g-3 mt-3">

          <Col
            md={6}
            lg={4}
          >

            <Form.Select
              value={
                selectedCategory
              }

              onChange={(e) => {

                const category =
                  e.target.value;


                setSearchParams(
                  prev => {

                    const params =
                      new URLSearchParams(
                        prev
                      );

                    if (
                      category === "All"
                    ) {

                      params.delete(
                        "category"
                      );

                    }
                    else {

                      params.set(
                        "category",
                        category
                      );

                    }

                    return params;

                  }
                );

              }}
            >

              {categories.map(
                category => (

                  <option
                    key={category}
                    value={category}
                  >

                    {category}

                  </option>

                )
              )}

            </Form.Select>

          </Col>


          <Col
            md={6}
            lg={4}
          >

            <Form.Select
              value={
                priceRange
              }

              onChange={(e) =>
                setPriceRange(
                  e.target.value
                )
              }
            >

              <option value="ALL">
                All Prices
              </option>

              <option value="0-25">
                £0 - £25
              </option>

              <option value="25-50">
                £25 - £50
              </option>

              <option value="50-100">
                £50 - £100
              </option>

              <option value="100+">
                £100+
              </option>

            </Form.Select>

          </Col>

        </Row>


        {/* ====================================================
            PRODUCT GRID
            ==================================================== */}

        {paginated.length > 0 ? (

          <Row
            className="g-4 mt-4"
          >

            {paginated.map(
              p => (

                <Col
                  lg={3}
                  md={6}
                  key={p._id}
                >

                  <ProductCard
                    p={p}
                    badge={
                      getFeatureBadges(
                        p
                      )[0]
                    }
                  />

                </Col>

              )
            )}

          </Row>

        ) : (

          <div
            className="text-center py-5"
            style={{
              color:
                darkMode
                  ? "#cbd5e1"
                  : "#6b7280"
            }}
          >

            <h4>
              No products found
            </h4>

            <p>
              Try changing your
              search or filters.
            </p>

          </div>

        )}


        {/* ====================================================
            PAGINATION
            ==================================================== */}

        {totalPages > 1 && (

          <div
            className="d-flex justify-content-center align-items-center gap-2 mt-5"
          >

            <Button
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() =>
                setPage(
                  prev =>
                    Math.max(
                      1,
                      prev - 1
                    )
                )
              }
            >

              ← Previous

            </Button>


            <span
              style={{
                fontWeight: 700
              }}
            >

              Page {page} of {totalPages}

            </span>


            <Button
              variant="outline-secondary"
              disabled={
                page === totalPages
              }
              onClick={() =>
                setPage(
                  prev =>
                    Math.min(
                      totalPages,
                      prev + 1
                    )
                )
              }
            >

              Next →

            </Button>

          </div>

        )}

      </Container>


      {/* ======================================================
          NEWSLETTER
          ====================================================== */}

      <Container
        fluid
        className="py-5"
      >

        <div
          style={{
            background:
              "linear-gradient(135deg,#2563eb,#3b82f6)",
            borderRadius:
              "32px",
            padding:
              "60px 40px",
            color: "#fff",
            textAlign:
              "center",
            boxShadow:
              "0 25px 60px rgba(37,99,235,.25)"
          }}
        >

          <h2
            style={{
              fontWeight: 800,
              fontSize:
                "clamp(2rem,4vw,3rem)"
            }}
          >

            📩 Join Our Community

          </h2>


          <p
            style={{
              maxWidth:
                "650px",
              margin:
                "20px auto 35px",
              lineHeight:
                "1.8"
            }}
          >

            Subscribe for exclusive
            offers, new products,
            discounts and latest
            updates.

          </p>


          <Form
            onSubmit={
              handleNewsletterSubscribe
            }
          >

            <div
              style={{
                display:
                  "flex",
                gap: "15px",
                justifyContent:
                  "center",
                flexWrap:
                  "wrap"
              }}
            >

              <Form.Control
                type="email"
                placeholder=
                  "Enter your email"
                value={
                  newsletterEmail
                }
                onChange={
                  e =>
                    setNewsletterEmail(
                      e.target.value
                    )
                }
                required
                style={{
                  maxWidth:
                    "400px",
                  height:
                    "55px",
                  borderRadius:
                    "999px",
                  border: "none",
                  paddingLeft:
                    "25px"
                }}
              />


              <Button
                type="submit"
                style={{
                  height:
                    "55px",
                  padding:
                    "0 35px",
                  borderRadius:
                    "999px",
                  border: "none",
                  background:
                    "#111827",
                  fontWeight: 700
                }}
              >

                Subscribe →

              </Button>

            </div>

          </Form>


          {subscribed && (

            <div
              style={{
                marginTop:
                  "20px",
                fontWeight: 700,
                color:
                  "#d1fae5"
              }}
            >

              ✅ Thank you
              for subscribing!

            </div>

          )}


          <Row
            className=
              "mt-5 text-center"
          >

            <Col md={4}>

              <h3
                style={{
                  fontWeight: 800
                }}
              >

                20K+

              </h3>

              <p>
                Subscribers
              </p>

            </Col>


            <Col md={4}>

              <h3
                style={{
                  fontWeight: 800
                }}
              >

                Weekly

              </h3>

              <p>
                Exclusive Deals
              </p>

            </Col>


            <Col md={4}>

              <h3
                style={{
                  fontWeight: 800
                }}
              >

                100%

              </h3>

              <p>
                No Spam
              </p>

            </Col>

          </Row>

        </div>

      </Container>


      {/* ======================================================
          ERROR
          ====================================================== */}

      {error && (

        <div
          style={{
            color: "red",
            textAlign: "center",
            padding: "20px"
          }}
        >

          {error}

        </div>

      )}

    </div>

  );

}


export default Products;

